/**
 * Pure derivation functions for the site dashboard.
 *
 * All functions here are plain JS — no hooks, no selectors, no JSX.
 * They take explicit arguments and return derived values so they can be
 * called inside useMemo in the view layer and tested in isolation.
 *
 * Contains:
 * - sortRelays — sort a relay array by relay_number ascending
 * - deriveRelayCardLayout — partition visible relays into full cards and compact groups
 * - buildCommandRelays — fold secondary relay pairings into labeled options
 * - isVantageUnit — detect Vantage by canonical unit-model id
 * - shouldShowSensorCard — show the sensor card for every Vantage unit
 * - filterDashboardSensors — omit unit-level Common configuration rows
 * - getDisplayableSensorReadingTypes — list readings that have a current value
 * - hasDisplayableSensorReading — identify sensor rows with a current display value
 * - filterVisibleAlarms — drop admin-only alarms unless the user may view them
 * - getAlarmStatus — compute card status string from visible alarm list
 * - deriveSiteCardStatus — collapse device/radio/check-in/alarm state into the
 *   header's status token
 * - resolveRelayLoadType — resolve a relay's load-type catalog row, falling
 *   back to the customer type's default when the relay has none configured
 */

import { SMARTLINK_MODEL } from '@sophocles/utils/const';

// Severity helpers are inlined rather than imported from @sophocles/utils/alarmSeverity
// so this module remains importable directly by the Node test runner (which cannot
// resolve webpack aliases or directory-level MUI imports). Keep this ranking and the
// card-status map in lockstep with alarmSeverity.js by hand — `high` carries the same
// operational weight as `critical` (same card status, one rank below it).
const SEVERITY_ORDER = { critical: 0, high: 1, warning: 2, attention: 3, info: 4 };
const SEVERITY_CARD_STATUS = { critical: 'bad', high: 'bad', warning: 'warning', attention: 'purple', info: 'info' };
const UNCONFIGURED_RELAY_GROUP_SIZE = 4;

function compareSeverity(a, b) {
  // An unrecognized severity sorts last — one past the worst known rank.
  const unknownRank = Object.keys(SEVERITY_ORDER).length;
  return (SEVERITY_ORDER[a] ?? unknownRank) - (SEVERITY_ORDER[b] ?? unknownRank);
}

function getHighestSeverity(alarmStates, accessor) {
  let best = null;
  for (const state of alarmStates) {
    const sev = accessor(state) || 'warning';
    if (best == null || compareSeverity(sev, best) < 0) best = sev;
  }
  return best;
}

function getSeverityCardStatus(severity) {
  return SEVERITY_CARD_STATUS[severity] ?? 'warning';
}

/**
 * Sort a relay array by relay_number ascending.
 *
 * Returns a new array — never mutates the input.
 *
 * @param {Array} relays  raw relay objects from the unit snapshot
 * @returns {Array}
 */
export function sortRelays(relays) {
  if (!relays) return [];
  return [...relays].sort((a, b) => a.relay_number - b.relay_number);
}

/**
 * Partition the site dashboard's visible relays into full configured cards and
 * compact groups of up to four unconfigured relays.
 *
 * Paired secondaries remain represented by their primary relay card and are
 * excluded before setup-state classification. Both output buckets preserve
 * relay-number ordering.
 *
 * @param {Array} relays raw relay objects from the unit snapshot
 * @returns {{ configuredRelays: Array, unconfiguredRelayGroups: Array<Array> }}
 */
export function deriveRelayCardLayout(relays) {
  const sortedRelays = sortRelays(relays);
  const secondaryRelayIds = new Set(sortedRelays.map(r => r.secondary_relay_id).filter(id => id != null));
  const visibleRelays = sortedRelays.filter(relay => !secondaryRelayIds.has(relay.id));
  const configuredRelays = [];
  const unconfiguredRelays = [];

  visibleRelays.forEach(relay => {
    const target = (relay.loads?.length ?? 0) > 0 ? configuredRelays : unconfiguredRelays;
    target.push(relay);
  });

  const unconfiguredRelayGroups = [];
  for (let index = 0; index < unconfiguredRelays.length; index += UNCONFIGURED_RELAY_GROUP_SIZE) {
    unconfiguredRelayGroups.push(unconfiguredRelays.slice(index, index + UNCONFIGURED_RELAY_GROUP_SIZE));
  }

  return { configuredRelays, unconfiguredRelayGroups };
}

/**
 * Build the relay options list for the CommandsBlade.
 *
 * Folds each relay whose id appears as another relay's secondary_relay_id
 * (the "secondary" half of a ganged pair) into its primary entry rather
 * than listing it separately. Labels ganged pairs as "Relay N+M".
 *
 * @param {Array}  relays       sorted relay array (output of sortRelays)
 * @param {string} relayLabel   translated label string (e.g. "Relay")
 * @returns {Array<{value: number, label: string}>}
 */
export function buildCommandRelays(relays, relayLabel) {
  if (!relays.length) return [];
  const relayById = new Map(relays.map(r => [r.id, r]));
  const pairedSecondaryIds = new Set(relays.map(r => r.secondary_relay_id).filter(id => id != null));
  return relays
    .filter(r => !pairedSecondaryIds.has(r.id))
    .map(r => {
      const secondaryNum = r.secondary_relay_id != null ? relayById.get(r.secondary_relay_id)?.relay_number : null;
      const label = secondaryNum
        ? `${relayLabel} ${r.relay_number}+${secondaryNum}`
        : `${relayLabel} ${r.relay_number}`;
      return { value: r.relay_number, label };
    });
}

/**
 * Detect whether a unit carries the canonical Vantage model identity.
 *
 * @param {Object|null} unit site unit from the dashboard snapshot
 * @returns {boolean}
 */
export function isVantageUnit(unit) {
  return unit?.model?.id === SMARTLINK_MODEL.VANTAGE;
}

/**
 * Determine whether the dashboard should render its sensor card.
 *
 * A Vantage with no reported rows still exposes its sensor configuration.
 * Other unit families retain a sensor card when their contract supplies rows.
 *
 * @param {Object|null} unit site unit from the dashboard snapshot
 * @returns {boolean}
 */
export function shouldShowSensorCard(unit) {
  return isVantageUnit(unit) || (unit?.sensors?.length ?? 0) > 0;
}

/**
 * Return only physical/display sensor rows from a dashboard snapshot.
 *
 * Common is unit-level Vantage configuration rather than dashboard inventory.
 * The numeric check accepts both normalized and legacy snapshot shapes.
 *
 * @param {Array|null} sensors sensor rows from unit.sensors[]
 * @returns {Array}
 */
export function filterDashboardSensors(sensors) {
  return (sensors ?? []).filter(
    sensor => sensor.sensor_type !== 'common' && sensor.sensor_type !== 5 && sensor.sensor_type_id !== 5,
  );
}

/**
 * List the reading types that have a current display or socket-path raw value.
 *
 * The actual values are authoritative. Returning the individual types lets
 * the card omit a missing temperature or humidity value without hiding the
 * configured sensor itself.
 *
 * @param {Object|null} sensor sensor row from unit.sensors[]
 * @returns {Array<'temperature'|'humidity'>}
 */
export function getDisplayableSensorReadingTypes(sensor) {
  return ['temperature', 'humidity'].filter(
    readingType => sensor?.[readingType]?.value != null || sensor?.[readingType]?.raw != null,
  );
}

/**
 * Determine whether a sensor row has at least one displayable reading.
 *
 * Either a display value or a socket-path raw value keeps the detailed
 * reading row visible; rows without either render as compact name-only
 * entries.
 *
 * @param {Object|null} sensor sensor row from unit.sensors[]
 * @returns {boolean}
 */
export function hasDisplayableSensorReading(sensor) {
  return getDisplayableSensorReadingTypes(sensor).length > 0;
}

/**
 * Filter alarm states to only those visible to the current user.
 *
 * Admin-only alarms (def.is_public === false) are hidden unless the user may
 * view admin alarms — the same gate the REST path enforces via the backend
 * `view_admin_alarms` permission. Unknown alarm ids (no matching def) are
 * treated as visible.
 *
 * @param {Array}  alarms     alarm state objects from the snapshot ({ alarm: {id, name}, ... })
 * @param {Array}  alarmDefs  alarm definition objects from the reference catalog
 * @param {boolean} canViewAdminAlarms  whether the current user may see admin-only alarms
 * @returns {Array}
 */
export function filterVisibleAlarms(alarms, alarmDefs, canViewAdminAlarms) {
  const defById = new Map(alarmDefs.map(d => [d.id, d]));
  return alarms.filter(state => {
    const def = defById.get(state.alarm?.id);
    return !(def?.is_public === false && !canViewAdminAlarms);
  });
}

/**
 * Compute the card status string for an alarm set.
 *
 * Returns 'good' when there are no visible alarms; otherwise resolves the
 * highest severity from the definition catalog and maps it to a card status
 * token ('bad', 'warning', 'purple', 'info').
 *
 * @param {Array}  visibleAlarms  filtered alarm state objects
 * @param {Array}  alarmDefs      alarm definition objects from the reference catalog
 * @returns {'good'|'bad'|'warning'|'purple'|'info'}
 */
export function getAlarmStatus(visibleAlarms, alarmDefs) {
  if (visibleAlarms.length === 0) return 'good';
  const defById = new Map(alarmDefs.map(d => [d.id, d]));
  const highestSeverity = getHighestSeverity(
    visibleAlarms,
    state => defById.get(state.alarm?.id)?.severity ?? 'warning',
  );
  return getSeverityCardStatus(highestSeverity ?? 'warning');
}

/**
 * Compute the site dashboard header's status token.
 *
 * A site ODL has never heard from reads neutral ('disabled') rather than green
 * or amber. That covers all three ways a site can be silent from birth: no
 * device installed, a device with no radio attached, and a radio that has never
 * sent a status. 'warning' is reserved for a site that reported at some point
 * and has since gone quiet, so a newly installed site is never presented as
 * having missed a check-in. Active alarms outrank connectivity, matching the
 * site grid's status ladder.
 *
 * @param {object}       args
 * @param {boolean}      args.hasDevice       - a relay controller is attached to the site.
 * @param {boolean}      args.hasRadio        - that device carries a radio.
 * @param {boolean}      args.hasEverReported - the device has sent at least one status.
 * @param {boolean|null} args.isRadioOnline   - the radio's online flag; null when there is no radio.
 * @param {number}       args.alarmCount      - number of alarms visible to this user.
 * @param {string}       args.alarmStatus     - card status token for that visible alarm set.
 * @returns {'good'|'bad'|'warning'|'purple'|'info'|'disabled'}
 */
export function deriveSiteCardStatus({ hasDevice, hasRadio, hasEverReported, isRadioOnline, alarmCount, alarmStatus }) {
  if (!hasDevice) return 'disabled';
  if (alarmCount > 0) return alarmStatus;
  if (!hasRadio || !hasEverReported) return 'disabled';
  return isRadioOnline === false ? 'warning' : 'good';
}

/**
 * Resolve a relay's load-type catalog row.
 *
 * A relay with no configured load_type_id falls back to the scope customer
 * type's default load type, so it renders as if it had that type rather than
 * showing no chip at all. A relay with its own load_type_id always resolves
 * to its own row, never the default.
 *
 * @param {Object}   relay              raw relay object ({ load_type_id, ... })
 * @param {Map}      loadTypeById       RelayLoadType catalog keyed by id
 * @param {?number}  defaultLoadTypeId  the scope customer type's default load
 *   type id, or null when no customer/type is in scope
 * @returns {Object|null}
 */
export function resolveRelayLoadType(relay, loadTypeById, defaultLoadTypeId) {
  const loadTypeId = relay.load_type_id ?? defaultLoadTypeId;
  return loadTypeId != null ? (loadTypeById.get(loadTypeId) ?? null) : null;
}
